import SkeletonTable from '@/components/SkeletonTable'
import { groupLogsByDate } from '@/features/AuditLogs/utils/auditLogUtils'
import type { AuditLogs } from '@/generated/http-clients/v3ApiSchemas'
import { Table, Text } from '@chakra-ui/react'
import { Fragment, type ReactNode, useMemo } from 'react'

/**
 * Public props shared by the concrete audit-log tables (the generic audit table
 * and the license history table). They expose the same data/selection contract;
 * only their columns and row rendering differ.
 */
export interface AuditLogTableProps {
  logs: AuditLogs[]
  isLoading: boolean
  isError: boolean
  isEmpty: boolean
  skeletonWidths: number[]
  selectedLogId?: string
  onRowClick: (log: AuditLogs) => void
  errorComponent: ReactNode
  emptyMessage: string
}

interface AuditLogTableShellProps {
  logs: AuditLogs[]
  isLoading: boolean
  isError: boolean
  isEmpty: boolean
  skeletonWidths: number[]
  errorComponent: ReactNode
  emptyMessage: string
  /** Number of columns, for the colSpan on state and date-header rows. */
  columnCount: number
  ariaLabel: string
  /** The `<Table.Row>` of column headers. */
  headerRow: ReactNode
  /**
   * Renders one data row. `showDate` is false when the rows are grouped under a
   * date header (so the row can show time only).
   */
  renderRow: (log: AuditLogs, showDate: boolean) => ReactNode
}

/**
 * Shared audit-log table structure: header, loading / error / empty states, and
 * date grouping. Column layout and row rendering are supplied by the caller, so
 * both the generic audit table and the license-tailored history table reuse the
 * same shell rather than duplicating these branches.
 */
export function AuditLogTableShell({
  logs,
  isLoading,
  isError,
  isEmpty,
  skeletonWidths,
  errorComponent,
  emptyMessage,
  columnCount,
  ariaLabel,
  headerRow,
  renderRow,
}: Readonly<AuditLogTableShellProps>) {
  const groupedLogs = useMemo(() => groupLogsByDate(logs), [logs])
  const hasMultipleDates = groupedLogs.length > 1

  return (
    <Table.Root size="sm" width="100%" aria-label={ariaLabel}>
      <Table.Header>{headerRow}</Table.Header>
      <Table.Body>
        {isLoading && <SkeletonTable skeletonWidths={skeletonWidths} />}

        {isError && (
          <Table.Row>
            <Table.Cell colSpan={columnCount}>{errorComponent}</Table.Cell>
          </Table.Row>
        )}

        {isEmpty && !isLoading && !isError && (
          <Table.Row>
            <Table.Cell colSpan={columnCount}>
              <Text color="gray.500" py={4} textAlign="center">
                {emptyMessage}
              </Text>
            </Table.Cell>
          </Table.Row>
        )}

        {!isLoading &&
          !isError &&
          !isEmpty &&
          groupedLogs.map((group) => (
            <Fragment key={group.date}>
              {/* Date header row - only show if multiple dates */}
              {hasMultipleDates && (
                <Table.Row bg="gray.50" _hover={{ bg: 'gray.50' }}>
                  <Table.Cell colSpan={columnCount} py={2}>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="gray.600"
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      {group.date}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              )}
              {group.logs.map((log) => (
                <Fragment key={log.id}>
                  {renderRow(log, !hasMultipleDates)}
                </Fragment>
              ))}
            </Fragment>
          ))}
      </Table.Body>
    </Table.Root>
  )
}
